import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { NewInboxPerNumberStore } from 'entities/Phone/ui/AssignNumberCommon/ui/NewInboxPerNumber/store/NewInboxPerNumberStore'
import { type IResponseNumber } from 'entities/Phone/api/types'
import { NewInboxStore } from '../ui/NewInboxContent/store/newInboxStore'

export type IAssignType = 'existing' | 'new' | 'new_per_number' | 'later'

const ASSIGN_NUMBER_TABS: Record<IAssignType, { key: IAssignType; name: string }> = {
  existing: { key: 'existing', name: 'Existing inbox' },
  new: { key: 'new', name: 'New inbox' },
  new_per_number: { key: 'new_per_number', name: 'New inbox per number' },
  later: { key: 'later', name: 'Assign later' },
}

export type IAssignNumberCommonStoreConfig = {
  nextClickHandler?: () => void | Promise<void>
  nextButtonName?: string
  allowedTabs?: IAssignType[]
  hideSubmit?: boolean
  numbers?: IResponseNumber[]
}

export class AssignNumberCommonStore {
  private readonly _newInboxStore: NewInboxStore
  private _activeInboxId: number | null = null
  private _activeTabKey = ASSIGN_NUMBER_TABS.existing.key
  private _isNextStepProcessing = false

  private _allTeamInboxesMap: Map<number, IResponseInbox> = new Map()
  newInboxPerNumberStore

  constructor(private _config: IAssignNumberCommonStoreConfig, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this._newInboxStore = new NewInboxStore(this._entities)
    this.newInboxPerNumberStore = new NewInboxPerNumberStore(this._entities, _config.numbers || [])
    this.init()
  }

  get nextButtonName() {
    return this._config.nextButtonName || 'Next'
  }

  get hideSubmit() {
    return Boolean(this._config.hideSubmit)
  }

  private get _allowedTabs() {
    return this._config.allowedTabs || ['existing', 'new', 'later']
  }

  get newInboxStore() {
    return this._newInboxStore
  }

  get activeInboxId() {
    return this._activeInboxId
  }

  get activeInbox() {
    if (!this._activeInboxId) return

    return this._allTeamInboxesMap.get(this._activeInboxId)
  }

  get assignType(): IAssignType {
    return this._activeTabKey
  }

  get allTeamInboxes() {
    return Array.from(this._allTeamInboxesMap.values()).filter((item) => !item.is_rcs)
  }

  get activeTabKey() {
    return this._activeTabKey
  }

  get hasInbox() {
    return this.allTeamInboxes.length !== 0
  }

  get newInboxOnly() {
    return this._allowedTabs.length === 1 && this._allowedTabs[0] === 'new'
  }

  get tabs() {
    return this._allowedTabs
      .filter((tab) =>
        tab === 'new_per_number' ? this._config.numbers && this._config.numbers.length > 1 : true
      )
      .map((tab) => ({ ...ASSIGN_NUMBER_TABS[tab] }))
  }

  get disabledButton() {
    if (this.assignType === 'existing') {
      return Boolean(!this._activeInboxId || this._isNextStepProcessing)
    }

    if (this.assignType === 'new') {
      return Boolean(this._newInboxStore.nextDisabled || this._isNextStepProcessing)
    }

    if (this.assignType === 'new_per_number') {
      return Boolean(this.newInboxPerNumberStore.nextDisabled || this._isNextStepProcessing)
    }

    return this._isNextStepProcessing
  }

  get loadingButton() {
    return this._isNextStepProcessing
  }

  init = async () => {
    await this.fetchAllInboxes()
    this.setInitialActiveTabKey()
  }

  setInitialActiveTabKey = () => {
    const isActiveTabExistKey = this.tabs.some((tab) => tab.key === this._activeTabKey)

    if (!isActiveTabExistKey) {
      const firstTab = this.tabs[0] ?? ASSIGN_NUMBER_TABS.new
      this._activeTabKey = firstTab.key
    }
  }

  setActiveTabKey = (key: IAssignType) => {
    this._activeTabKey = key
  }

  private _loadingFetchAllInboxes = false
  get loadingFetchAllInboxes() {
    return this._loadingFetchAllInboxes
  }
  private _setLoadingFetchAllInboxes = (value: boolean) => {
    this._loadingFetchAllInboxes = value
  }

  fetchAllInboxes = async () => {
    try {
      this._setLoadingFetchAllInboxes(true)
      const { data } = await this._entities.inboxesStore.getTeamsAll()

      runInAction(() => {
        data.forEach((item) => {
          this._allTeamInboxesMap.set(item.id, item)
        })
      })
    } catch (e) {
      logger.error(e)
    } finally {
      this._setLoadingFetchAllInboxes(false)
    }
  }

  setActiveInbox = (id: number | null) => {
    this._activeInboxId = id
  }

  nextClick = async () => {
    if (this._isNextStepProcessing) return
    this._isNextStepProcessing = true

    try {
      await this._config.nextClickHandler?.()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isNextStepProcessing = false
      })
    }
  }

  reset = () => {
    this.setActiveInbox(null)
    this.newInboxStore.reset()
    this.setInitialActiveTabKey()
  }
}
